import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { 
  Crown, Check, Star, ArrowRight, RefreshCw, CheckCircle, XCircle,
  CreditCard, Receipt, Calendar, Clock, Shield, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const getButtonStyle = (tierId, isCurrentPlan, isDowngrade, color) => {
  if (isCurrentPlan) return 'bg-slate-100 text-slate-500 cursor-default';
  if (tierId === 'free') return 'bg-slate-100 text-slate-500 cursor-default';
  if (isDowngrade) return 'bg-slate-100 text-slate-400 cursor-not-allowed';
  return `bg-gradient-to-r ${color} text-white hover:opacity-90 shadow-md`;
};

const ButtonLabel = ({ tierId, isCurrentPlan, isDowngrade, isLoading }) => {
  if (isLoading) return <><RefreshCw className="h-4 w-4 animate-spin" />Processing...</>;
  if (isCurrentPlan) return 'Current Plan';
  if (tierId === 'free') return 'Free Forever';
  if (isDowngrade) return 'Contact Support';
  return <>Upgrade <ArrowRight className="h-4 w-4" /></>;
};

const TIERS = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    period: 'forever',
    description: 'Get started with basic features',
    features: [
      'Basic profile & verification',
      'Browse job listings',
      'Community access',
      'Member directory',
      'Education hub access'
    ],
    color: 'from-slate-500 to-slate-600',
    border: 'border-slate-200',
    badge: 'bg-slate-100 text-slate-700'
  },
  {
    id: 'starter',
    name: 'Starter',
    price: 29,
    period: '/month',
    description: 'For individual carriers getting started',
    features: [
      'Everything in Free',
      'Priority job listings',
      'Basic analytics dashboard',
      'Email support',
      'Enhanced profile visibility'
    ],
    color: 'from-blue-500 to-blue-600',
    border: 'border-blue-200',
    badge: 'bg-blue-100 text-blue-700'
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 79,
    period: '/month',
    description: 'For growing carriers and fleets',
    features: [
      'Everything in Starter',
      'AI-powered job matching',
      'Priority support',
      'Badge verification boost',
      'Advanced analytics & reports',
      'Custom profile branding'
    ],
    color: 'from-purple-500 to-purple-600',
    border: 'border-purple-200',
    badge: 'bg-purple-100 text-purple-700',
    popular: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 199,
    period: '/month',
    description: 'For established carriers and organizations',
    features: [
      'Everything in Professional',
      'Dedicated account manager',
      'Custom integrations & API access',
      'Unlimited team members',
      'White-glove onboarding',
      'SLA guarantees'
    ],
    color: 'from-amber-500 to-amber-600',
    border: 'border-amber-200',
    badge: 'bg-amber-100 text-amber-700'
  }
];

const Subscription = () => {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const currentTier = user?.membership_tier || 'free';
  const currentTierData = TIERS.find(t => t.id === currentTier) || TIERS[0];
  const tierIndex = TIERS.findIndex(t => t.id === currentTier);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
    fetchBillingHistory();
  }, [searchParams]);

  const fetchBillingHistory = async () => {
    try {
      const res = await api.get('/stripe/billing-history');
      setBillingHistory(res.data.transactions || []);
    } catch (error) {
      // Billing history non-critical
    } finally {
      setHistoryLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      setPaymentStatus('timeout');
      return;
    }
    try {
      const res = await api.get(`/stripe/checkout-status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setPaymentStatus('success');
        await refreshUser();
        await fetchBillingHistory();
        toast.success('Subscription upgraded successfully!');
        return;
      } else if (res.data.status === 'expired') {
        setPaymentStatus('expired');
        return;
      }
      setPaymentStatus('processing');
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      setPaymentStatus('error');
    }
  };

  const handleUpgrade = async (tierId) => {
    if (tierId === 'free' || tierId === currentTier) return;
    setLoading(tierId);
    try {
      const originUrl = window.location.origin;
      const res = await api.post('/stripe/create-checkout', { tier_id: tierId, origin_url: originUrl });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1">
        {/* Hero */}
        <div className="relative overflow-hidden bg-slate-900">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>
          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur rounded-full text-sm text-slate-300 mb-4 border border-white/10">
                <Crown className="h-4 w-4" />
                Subscription Management
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-3" data-testid="subscription-title">
                Manage Your Plan
              </h1>
              <p className="text-slate-400 max-w-xl mx-auto text-base sm:text-lg">
                Upgrade to unlock premium features. All plans include a 14-day money-back guarantee.
              </p>
            </motion.div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-8">
          {/* Payment Status Banner */}
          {paymentStatus && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
              className={`p-4 rounded-xl flex items-center gap-3 ${
                paymentStatus === 'success' ? 'bg-green-50 border border-green-200' :
                paymentStatus === 'processing' ? 'bg-blue-50 border border-blue-200' :
                'bg-red-50 border border-red-200'
              }`} data-testid="payment-status-banner">
              {paymentStatus === 'success' && <><CheckCircle className="h-5 w-5 text-green-600" /><p className="text-green-800 font-medium">Payment successful! Your subscription has been upgraded.</p></>}
              {paymentStatus === 'processing' && <><RefreshCw className="h-5 w-5 text-blue-600 animate-spin" /><p className="text-blue-800 font-medium">Processing your payment...</p></>}
              {paymentStatus === 'error' && <><XCircle className="h-5 w-5 text-red-600" /><p className="text-red-800 font-medium">Payment verification failed. Contact support.</p></>}
              {paymentStatus === 'expired' && <><XCircle className="h-5 w-5 text-red-600" /><p className="text-red-800 font-medium">Payment session expired. Please try again.</p></>}
              {paymentStatus === 'timeout' && <><Clock className="h-5 w-5 text-amber-600" /><p className="text-amber-800 font-medium">Payment status check timed out. Check your email for confirmation.</p></>}
            </motion.div>
          )}

          {/* Current Plan Card */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm"
            data-testid="current-plan-card"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${currentTierData.color} flex items-center justify-center shadow-sm`}>
                  <Crown className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Current Plan</p>
                  <h2 className="text-xl font-bold text-slate-900">{currentTierData.name}</h2>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {currentTier === 'free' ? 'No charges — free forever' : `$${currentTierData.price}/month`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-green-500" />
                <span className="text-sm text-slate-600">Secured by Stripe</span>
              </div>
            </div>
            {currentTier !== 'free' && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="text-sm text-slate-500">Your plan includes:</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {currentTierData.features.map(f => (
                    <span key={f} className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-50 rounded-full text-xs text-slate-600 border border-slate-100">
                      <Check className="h-3 w-3 text-green-500" />{f}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </motion.div>

          {/* Tier Cards */}
          <div>
            <h2 className="text-lg font-bold text-slate-900 mb-4">Available Plans</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {TIERS.map((tier, idx) => {
                const isCurrentPlan = tier.id === currentTier;
                const isDowngrade = tierIndex >= idx && !isCurrentPlan;
                return (
                  <motion.div
                    key={tier.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.06 }}
                    className={`relative bg-white rounded-2xl border-2 p-5 flex flex-col shadow-sm hover:shadow-lg transition-shadow ${
                      tier.popular ? 'border-purple-400 ring-2 ring-purple-100' : tier.border
                    } ${isCurrentPlan ? 'ring-2 ring-blue-400' : ''}`}
                    data-testid={`tier-${tier.id}`}
                  >
                    {tier.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-purple-600 text-white text-xs font-bold rounded-full whitespace-nowrap">
                        Most Popular
                      </div>
                    )}
                    {isCurrentPlan && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-blue-600 text-white text-xs font-bold rounded-full whitespace-nowrap">
                        Current Plan
                      </div>
                    )}

                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium self-start mb-3 ${tier.badge}`}>
                      <Star className="h-3.5 w-3.5" />{tier.name}
                    </div>

                    <div className="mb-3">
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-slate-900">${tier.price}</span>
                        {tier.period !== 'forever' && <span className="text-slate-500 text-sm">{tier.period}</span>}
                      </div>
                      <p className="text-sm text-slate-500 mt-1">{tier.description}</p>
                    </div>

                    <ul className="space-y-2 mb-5 flex-1">
                      {tier.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-2 text-sm text-slate-700">
                          <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={() => handleUpgrade(tier.id)}
                      disabled={isCurrentPlan || tier.id === 'free' || loading === tier.id || isDowngrade}
                      className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 ${getButtonStyle(tier.id, isCurrentPlan, isDowngrade, tier.color)} disabled:opacity-50`}
                      data-testid={`upgrade-${tier.id}`}
                    >
                      <ButtonLabel tierId={tier.id} isCurrentPlan={isCurrentPlan} isDowngrade={isDowngrade} isLoading={loading === tier.id} />
                    </button>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Billing History */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm"
            data-testid="billing-history"
          >
            <div className="flex items-center gap-2 mb-5">
              <Receipt className="h-5 w-5 text-slate-600" />
              <h2 className="text-lg font-bold text-slate-900">Billing History</h2>
            </div>

            {historyLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-5 w-5 text-slate-400 animate-spin" />
              </div>
            ) : billingHistory.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                <CreditCard className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500 text-sm font-medium">No billing history yet</p>
                <p className="text-slate-400 text-xs mt-1">Your payment transactions will appear here</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="billing-table">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left py-2.5 px-3 text-slate-500 font-medium">Date</th>
                      <th className="text-left py-2.5 px-3 text-slate-500 font-medium">Plan</th>
                      <th className="text-left py-2.5 px-3 text-slate-500 font-medium">Amount</th>
                      <th className="text-left py-2.5 px-3 text-slate-500 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billingHistory.map((tx) => (
                      <tr key={tx.id || tx.session_id} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="py-3 px-3 text-slate-700">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-3.5 w-3.5 text-slate-400" />
                            {new Date(tx.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        </td>
                        <td className="py-3 px-3 text-slate-700 capitalize font-medium">{tx.tier_id || 'Payment'}</td>
                        <td className="py-3 px-3 text-slate-900 font-semibold">${(tx.amount || 0).toFixed(2)}</td>
                        <td className="py-3 px-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            tx.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                            tx.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {tx.payment_status === 'paid' && <CheckCircle className="h-3 w-3" />}
                            {tx.payment_status === 'pending' && <Clock className="h-3 w-3" />}
                            {tx.payment_status || 'Unknown'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>

          {/* Footer info */}
          <div className="text-center text-sm text-slate-500 space-y-1 pb-4">
            <p>All plans include SSL encryption, 99.9% uptime, and GDPR compliance.</p>
            <p>Questions? Contact <a href="mailto:telly.arnold@tlagroupllc.com" className="text-blue-600 hover:underline">telly.arnold@tlagroupllc.com</a></p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Subscription;
